import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faSpinner, 
  faCheckCircle, 
  faTimesCircle, 
  faChevronRight, 
  faChevronDown, 
  faLayerGroup, 
  faDollarSign, 
  faBuilding, 
  faSearch, 
  faFilter,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';
import { useCustomLoading } from '../hooks/useCustomLoading';
import RepairProcessTable from '../components/RepairProcessTable';
import ProjectCompletionModal from '../components/ProjectCompletionModal';
import PanelBayPaintingModal from '../components/PanelBayPaintingModal';

/**
 * RepairProjectExecution Page
 * 
 * Similar UI to Enhanced Simulation Results for consistency
 * Handles repair project execution workflow with inspection data
 */
const RepairProjectExecution = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useCustomLoading();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Project data
  const [project, setProject] = useState(null);
  const [spans, setSpans] = useState([]);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [completedSpansCount, setCompletedSpansCount] = useState(0);

  // UI State
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isProjectOverviewCollapsed, setIsProjectOverviewCollapsed] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [activePanelBaySpan, setActivePanelBaySpan] = useState(null);

  // Fetch execution details on mount
  useEffect(() => {
    fetchExecutionDetails();
  }, [id]);

  const fetchExecutionDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/repair-projects/${id}/execution`);
      
      if (response.data.success) {
        const { project, elements, spans, progress_percentage, completed_elements, completed_spans } = response.data.data;
        setProject(project);
        // Use elements if available, fallback to spans for backward compatibility
        const elementsData = elements || spans || [];
        setSpans(elementsData);
        setProgressPercentage(progress_percentage);
        setCompletedSpansCount(completed_elements || completed_spans || 0);
      }
    } catch (err) {
      console.error('Error fetching execution details:', err);
      setError(err.response?.data?.message || 'Failed to load project execution details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.put(`/repair-projects/${id}/status`, {
        status: newStatus
      });
      
      if (response.data.success) {
        setSuccess(`Project status updated to ${newStatus}`);
        setProject(prev => ({ ...prev, status: newStatus }));
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setError(err.response?.data?.message || 'Failed to update project status');
    } finally {
      setLoading(false);
    }
  };

  // Removed: handleSpanComplete - individual element completion removed
  // Elements are now only marked complete when entire project is completed

  const handleOpenPanelBayModal = (span) => {
    setActivePanelBaySpan(span);
    setError(null);
  };

  const handlePanelBaySave = async (panelBayUpdates) => {
    if (!activePanelBaySpan) {
      return;
    }

    const targetElementId = activePanelBaySpan.element_id || activePanelBaySpan.span_id || activePanelBaySpan.id;

    try {
      showLoading('Saving panel bay updates...');
      await api.put(`/repair-projects/${id}/spans/${targetElementId}/panel-bays`, {
        panelBays: panelBayUpdates
      });
      setSuccess('Panel bay updates saved successfully');
      setTimeout(() => setSuccess(null), 3000);
      await fetchExecutionDetails();
      setActivePanelBaySpan(null);
    } catch (err) {
      console.error('Error updating panel bays:', err);
      const message = err.response?.data?.message || 'Failed to update panel bays';
      setError(message);
      throw new Error(message);
    } finally {
      hideLoading();
    }
  };

  const handleProjectCompletion = async (completionData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post(`/repair-projects/${id}/complete`, completionData);
      
      if (response.data.success) {
        setSuccess('Project completed successfully! Span baselines have been updated.');
        setTimeout(() => {
          navigate('/repair-projects');
        }, 2000);
      }
    } catch (err) {
      console.error('Error completing project:', err);
      setError(err.response?.data?.message || 'Failed to complete project');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2
    }).format(value);
  };

  if (loading && !project) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} spin className="text-4xl text-blue-500 mb-4" />
          <p className="text-gray-600">Loading project execution details...</p>
        </div>
      </div>
    );
  }

  if (error && !project) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          <FontAwesomeIcon icon={faTimesCircle} className="mr-2" />
          {error}
        </div>
        <button
          onClick={() => navigate('/repair-projects')}
          className="text-blue-600 hover:text-blue-800"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back to Projects
        </button>
      </div>
    );
  }

  const totalSpans = spans.length;
  const totalEstimatedCost = spans.reduce((sum, span) => sum + (parseFloat(span.estimated_cost) || 0), 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/repair-projects')}
            className="text-blue-600 hover:text-blue-800 mb-2 text-sm"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Back to Projects
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{project?.project_name || 'Repair Project Execution'}</h1>
          <p className="text-sm text-gray-600">Manage repair work and track progress</p>
        </div>
        <div className="flex items-center space-x-3">
          {/* Status Dropdown - Simplified to pending/completed */}
          <select
            value={project?.status || 'pending'}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            disabled={loading || project?.status === 'completed'}
          >
            <option value="pending">Pending</option>
            <option value="completed" disabled>Completed (use Complete Project button)</option>
          </select>

          {/* Complete Project Button - Show for pending projects */}
          {project?.status === 'pending' && (
            <button
              onClick={() => setShowCompletionModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
              Complete Project
            </button>
          )}
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
          {success}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          <FontAwesomeIcon icon={faTimesCircle} className="mr-2" />
          {error}
        </div>
      )}

      {/* Progress Bar */}
      <div className="mb-6 bg-white rounded-lg shadow-sm border p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Project Progress</span>
          <span className="text-sm font-bold text-gray-900">{progressPercentage}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-green-600 h-3 rounded-full transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        <div className="mt-2 text-xs text-gray-600">
          {completedSpansCount} of {totalSpans} {totalSpans === 1 ? 'element' : 'elements'} completed
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faLayerGroup} className="text-2xl text-blue-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Total Elements</p>
              <p className="text-2xl font-bold text-gray-900">{totalSpans}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faBuilding} className="text-2xl text-purple-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Bridge</p>
              <p className="text-2xl font-bold text-gray-900">{project?.bridge_name || 'N/A'}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faDollarSign} className="text-2xl text-green-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Estimated Cost</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalEstimatedCost)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Project Overview (Collapsible) */}
      <div className="bg-white rounded-lg shadow-sm border mb-6">
        <div 
          className="p-6 cursor-pointer flex items-center justify-between"
          onClick={() => setIsProjectOverviewCollapsed(!isProjectOverviewCollapsed)}
        >
          <div className="flex items-center">
            <FontAwesomeIcon 
              icon={isProjectOverviewCollapsed ? faChevronRight : faChevronDown} 
              className="mr-3 text-gray-500" 
            />
            <h2 className="text-lg font-semibold text-gray-900">1. Project Overview</h2>
          </div>
        </div>
        
        {!isProjectOverviewCollapsed && (
          <div className="px-6 pb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">Project Code</p>
                <p className="font-medium text-gray-900">{project?.project_code || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Planned Year</p>
                <p className="font-medium text-gray-900">{project?.planned_year || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Crews Count</p>
                <p className="font-medium text-gray-900">{project?.crews_count || 1}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Priority</p>
                <p className="font-medium text-gray-900 capitalize">{project?.priority || 'medium'}</p>
              </div>
            </div>
            {project?.description && (
              <div className="mt-4">
                <p className="text-sm text-gray-600">Description</p>
                <p className="text-gray-900 mt-1">{project.description}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Spans Execution (Main Section) */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              2. Repair Execution Details
            </h2>
          </div>
          <div className="text-sm text-gray-600 mb-4">
            Execute repair work element by element. Review inspection data and required processes for each element.
          </div>
          
          {/* Search and Filter Controls */}
          <div className="mb-4 flex items-center space-x-4">
            <div className="flex-1 relative">
              <FontAwesomeIcon 
                icon={faSearch} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
              />
              <input
                type="text"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search elements by code or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex items-center space-x-2">
              <FontAwesomeIcon icon={faFilter} className="text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Elements</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            {(searchTerm || filterStatus !== 'all') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterStatus('all');
                }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Clear
              </button>
            )}
          </div>
          
          {/* Repair Process Table */}
          <RepairProcessTable
            spans={spans}
            onManagePanelBays={handleOpenPanelBayModal}
            projectStatus={project?.status}
            searchTerm={searchTerm}
            filterStatus={filterStatus}
          />
        </div>
      </div>

      {/* Project Completion Modal */}
      <ProjectCompletionModal
        isOpen={showCompletionModal}
        onClose={() => setShowCompletionModal(false)}
        onComplete={handleProjectCompletion}
        project={project}
        completedSpansCount={completedSpansCount}
        totalSpansCount={totalSpans}
      />

      <PanelBayPaintingModal
        isOpen={Boolean(activePanelBaySpan)}
        span={activePanelBaySpan}
        onClose={() => setActivePanelBaySpan(null)}
        onSave={handlePanelBaySave}
      />
    </div>
  );
};

export default RepairProjectExecution;





